import { Alert, Form } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import {
  CustomButton,
  CustomForm,
  CustomFormItem,
  CustomImage,
  CustomInput,
  CustomMaskInput,
  CustomTextInput,
  Text,
  useText,
} from '@src/components';
import EditPhoneOrEmailFormModal from "./EditPhoneOrEmailFormModal";
import EditFirstnameOrSurnameFormModal from "./EditFirstnameOrSurnameFormModal";
import warning from '@src/assets/icons/icon-profil-warning.svg';
import { useCallback, useEffect, useState } from 'react';
import { formMailRegex, formPhoneRegex } from '@src/utils/formRule';
import { profileAlertKey } from '@src/utils/keys';
import { useSelector } from 'react-redux';

const CustomEditOutlined = ({onClick}) => {
  return (
    <EditOutlined 
      onClick={onClick}
      style={{  width: "48px", height: "48px", fontSize: "28px", marginRight: "-11px", padding: "9px 0", backgroundColor: '#f9f9f9', cursor: 'pointer'}} 
    />
  );
};

const PersonalInformation = ({ handleUpdate }) => {
  const { currentUser } = useSelector((state) => state?.user);
  const [form] = Form.useForm();
  const [alertIsVisible, setAlertIsVisible] = useState(true);
  const [currentUserObjectData, setCurrentUserObjectData] = useState(null);
  const [currentUserPhoneOrEmailObjectData, setCurrentUserPhoneOrEmailObjectData] = useState(null);

  const [userNameFormModalVisible, setUserNameFormModalVisible] = useState(false);
  const [phoneNumberOrEmailFormModalVisible, setPhoneNumberOrEmailModalVisible] = useState(false);

  useEffect(() => {
    const isAlertVisible = window.localStorage.getItem(profileAlertKey);
    if (isAlertVisible) {
      setAlertIsVisible(false);
    }
  }, []);

  useEffect(() => {
    form.setFieldsValue(currentUser)
   }, [form, currentUser])

  const onFinish = useCallback(
    (values) => {
      try {
        const body = {
          id: currentUser?.id,
          name: values?.name?.trim(),
          surName: values?.surName?.trim(),
          userName: values?.userName?.trim(),
          mobilePhones: values.mobilePhones?.trim()
            ?.replace(/\)/g, '')
            ?.replace(/\(/g, '')
            ?.replace(/-/g, '')
            ?.replace(/ /g, ''),
          email: values?.email?.trim(),
        };

        handleUpdate(body);
      } catch (error) {}
    },
    [currentUser, handleUpdate],
  );

  const onClickAlert = useCallback(() => {
    window.localStorage.setItem(profileAlertKey, 'true');
    setAlertIsVisible(false);
  }, []);

  const onEdittCurrentUserNameOrSurName = ({id, name, inputName}) => {
    setCurrentUserObjectData({id, name, inputName})
    setUserNameFormModalVisible(true);
  };

  const onEdittCurrentUserPhoneOrEmail = ({id, name, inputName}) => {
    setCurrentUserPhoneOrEmailObjectData({id, name, inputName})
    setPhoneNumberOrEmailModalVisible(true);
  }

  return (
    <div>
      <div className="header-content">
        <h4>
          <Text t="personalInfo" />
        </h4>
      </div>
      <div className="personal-information-form">
        {alertIsVisible && (
          <Alert
            message={
              <div className="alert-message-contain">
                <CustomImage src={warning} />
                <Text t="personalInfoAlertMessage" />
              </div>
            }
            banner
            showIcon={false}
            className="custom-alert-warning"
            closable
            onClose={onClickAlert}
          />
        )}

        <div className="personal-form">
          <CustomForm
            name="personalInformation"
            onFinish={onFinish}
            form={form}
            initialValues={{
              name: currentUser?.name,
              surName: currentUser?.surName,
              userName: currentUser?.userName,
              mobilePhones: currentUser?.mobilePhones,
              email: currentUser?.email,
            }}
            awaitLoading={!currentUser?.id}
            autoComplete="off"
            layout={'vertical'}
          >
            <div className="row ">
              <div className="col-md-6">
                <CustomFormItem
                  label={<Text t="name" />}
                  name="name"
                  key={`firstname-${currentUser.id}`}
                  rules={[{ message: <Text t="pleaseEnterName" /> }]}
                >
                  <CustomTextInput id="myProfile-personalInformation-name-textInput" data-testid="name" placeholder={useText('name')} suffix={<CustomEditOutlined onClick={()=> onEdittCurrentUserNameOrSurName({id: currentUser.id, name: currentUser.name, inputName: 'name'})} />} disabled/>
                </CustomFormItem>
              </div>

              <div className="col-md-6">
                <CustomFormItem
                  label={<Text t="surname" />}
                  name="surName"
                  key={`surname-${currentUser.id}`} 
                  rules={[{ message: <Text t="pleaseEnterSurname" /> }]}
                >
                  <CustomTextInput id="myProfile-personalInformation-surName-textInput" data-testid="surName" placeholder={useText('surname')} suffix={<CustomEditOutlined onClick={()=> onEdittCurrentUserNameOrSurName({id: currentUser.id, name: currentUser.surName, inputName: 'surName'})} />} disabled/>
                </CustomFormItem>
              </div>
            </div>

            <div className="row ">
              <div className="col-md-6">
                <CustomFormItem
                  label={<Text t="loginUsernameInputForgotPassword" />}
                  name="userName"
                >
                  <CustomInput
                    id="myProfile-personalInformation-userName-input"
                    data-testid="userName"
                    disabled
                    placeholder={useText('loginUsernameInputForgotPassword')}
                  />
                </CustomFormItem>
              </div>

              <div className="col-md-6">
                <CustomFormItem
                  label={<Text t="phoneNumber" />}
                  name="mobilePhones"
                  rules={[
                    { message: <Text t="pleaseEnterPhone" /> },
                    { validator: formPhoneRegex, message: <Text t="enterValidPhoneNumber" /> },
                  ]}
                >
                  <CustomMaskInput id="myProfile-personalInformation-mobilePhones-maskInput" mask={'+\\90 (999) 999 99 99'} disabled>
                    <CustomInput id="myProfile-personalInformation-mobilePhones-input" data-testid="mobilePhones" placeholder={useText('phoneNumber')} suffix={<CustomEditOutlined onClick={() => onEdittCurrentUserPhoneOrEmail({id: currentUser.id, name: currentUser.mobilePhones, inputName: 'mobilePhones'})} />} />
                  </CustomMaskInput>
                </CustomFormItem>
              </div>
            </div>

            <div className="row ">
              <div className="col-md-6">
                <CustomFormItem
                  label={<Text t="emailAdress" />}
                  name="email"
                  rules={[
                    { message: <Text t="pleaseEnterEmail" /> },
                    { validator: formMailRegex, message: <Text t="enterValidEmail" /> },
                  ]}
                >
                  <CustomInput id="myProfile-personalInformation-email-input" data-testid="email" placeholder={useText('email')} suffix={<CustomEditOutlined onClick={()=> onEdittCurrentUserPhoneOrEmail({id: currentUser.id, name: currentUser.email, inputName: 'email'})} />} disabled/>
                </CustomFormItem>
              </div>
            </div>

          </CustomForm>
        </div>
      </div>
      {currentUserObjectData !== null && <EditFirstnameOrSurnameFormModal
          modalVisible={userNameFormModalVisible}
          handleModalVisible={setUserNameFormModalVisible}
          user={currentUserObjectData}
          currentUser={currentUser}
          handleUpdate={handleUpdate}

      />}
      {currentUserPhoneOrEmailObjectData !== null && <EditPhoneOrEmailFormModal
          modalVisible={phoneNumberOrEmailFormModalVisible}
          handleModalVisible={setPhoneNumberOrEmailModalVisible}
          user={currentUserPhoneOrEmailObjectData}
          currentUser={currentUser}
      />}
    </div>
  );
};

export default PersonalInformation;
